import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Accordion } from '@frontend/design-system';
import { MessageTemplateSelectionCard } from '../../components/MessageTemplateSelectionCard';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { MessageTemplateCardImg } from '../../images';

type MessageTemplateType = 'Request Received' | 'Appointment Confirmed';

export const MessageTemplatesGlobalSettings = () => {
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });
  const { t } = useTranslation('messageTemplatesGlobalSettings');
  const { selectedLocationIds, getScopeName } = useAppScopeStore();
  const { navigate } = useSettingsNavigate();

  const { getOnlineSchedulingReadyLocationIds } = useSchedulingLocationInfo();
  const onlineSchedulingReadyLocationIds = getOnlineSchedulingReadyLocationIds();

  const navigateTo = (messageTemplateType: MessageTemplateType, locationId: string) => {
    const route =
      messageTemplateType === 'Request Received'
        ? '/schedule/message-templates/request-received/:locationId'
        : '/schedule/message-templates/appointment-confirmed/:locationId';

    navigate({
      to: route,
      params: { locationId: locationId ?? '' },
    });
  };

  return (
    <Accordion variant='location' showBoxShadow chevronSize={16} startingValue='first' size='large'>
      {selectedLocationIds?.map((locationId: string) => {
        const hasOnlineSchedulingCF = onlineSchedulingReadyLocationIds?.includes(locationId);

        return (
          <Accordion.Item value={locationId} key={locationId}>
            <Accordion.Header>
              <Accordion.Header.Location title={getScopeName(locationId)} />
            </Accordion.Header>
            <Accordion.Body css={{ padding: 0 }}>
              <div css={accordionBodyContainerStyles(!hasOnlineSchedulingCF, isMobile)}>
                {!!hasOnlineSchedulingCF && (
                  <MessageTemplateSelectionCard
                    title={t('Request Received')}
                    subtitle={t('This message automatically sends when a patient submits an appointment request.')}
                    imageElement={<MessageTemplateCardImg />}
                    onClick={() => navigateTo('Request Received', locationId)}
                  />
                )}
                <MessageTemplateSelectionCard
                  title={t('Appointment Confirmed')}
                  subtitle={t(
                    'This message automatically sends once an appointment has been approved by a member of your staff.'
                  )}
                  imageElement={<MessageTemplateCardImg />}
                  onClick={() => navigateTo('Appointment Confirmed', locationId)}
                />
              </div>
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};

const accordionBodyContainerStyles = (shouldCenterAlign: boolean, isMobile: boolean) =>
  css({
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'stretch',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: shouldCenterAlign ? 'center' : 'space-between',
    gap: theme.spacing(3),
    backgroundColor: theme.colors.neutral10,
  });
